import Chart, { YAxis, formatAmount, niceMax, useBoxWidth, withUnit, xLabelIndexes } from './Chart';

// §2.6 (rev 2.3) BurnUp: the running total against the running target. One accent line (with a faint wash under it) up to
// today; the target as an ink-2 rule from zero to the range's whole target; direct labels at the end only (the total so
// far, and the target), never a number on every point. Each point carries a title tooltip.
//   points: [{ key, label, amount, target }] in order (days or weeks); upTo: the index of the last point that has
//   happened (later points draw no line); unit 'words' | 'min'.
export default function BurnUp({ title, note, points, upTo = points.length - 1, unit = 'words', periodLabel = 'Day' }) {
  const [ref, w] = useBoxWidth(560);
  const W = Math.max(w, 280), H = 220, pl = 44, pt = 16, pb = 28;
  // On a phone the end labels sit above / below the line's end instead of beside it.
  const narrow = W < 420, pr = narrow ? 16 : 112;
  let run = 0, runT = 0;
  const cum = points.map(p => { run += p.amount || 0; runT += p.target || 0; return { ...p, cum: run, cumTarget: runT } });
  const hasTarget = points.some(p => p.target > 0);
  const last = Math.min(upTo, cum.length - 1);
  const max = niceMax(Math.max(hasTarget ? runT : 0, last >= 0 ? cum[last].cum : 0));
  const n = points.length;
  const x = i => pl + (W - pl - pr) * (n > 1 ? i / (n - 1) : 0.5);
  const y = v => pt + (H - pt - pb) * (1 - v / max);
  const labels = xLabelIndexes(n, W - pl - pr);
  const shown = cum.slice(0, last + 1);
  const line = shown.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.cum)}`).join(' ');
  const wash = shown.length > 1 ? `${line} L${x(last)},${y(0)} L${x(0)},${y(0)} Z` : '';
  const target = cum.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.cumTarget)}`).join(' ');
  const table = {
    columns: [
      { key: 'label', label: periodLabel },
      { key: 'amount', label: 'Approved', align: 'right', render: r => <span className="ui-num">{formatAmount(r.amount, unit)}</span> },
      { key: 'cum', label: 'Running total', align: 'right', render: r => <span className="ui-num">{formatAmount(r.cum, unit)}</span> },
      ...(hasTarget ? [{ key: 'cumTarget', label: 'Running target', align: 'right', render: r => <span className="ui-num">{formatAmount(r.cumTarget, unit)}</span> }] : []),
    ],
    rows: shown, rowKey: r => r.key,
  };
  return (
    <Chart title={title} note={note} table={table}>
      <div className="ui-chart__plot" ref={ref}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}: ${last >= 0 ? withUnit(cum[last].cum, unit) : 'nothing yet'}${hasTarget ? ` against a target of ${withUnit(runT, unit)}` : ''}.`}>
          <YAxis max={max} unit={unit} x0={pl} x1={W - pr} y={y} />
          {points.map((p, i) => labels.has(i) && <text key={p.key} className="ui-chart__axis" x={x(i)} y={H - 8} textAnchor="middle">{p.axis || p.label}</text>)}
          {hasTarget && <path className="ui-chart__target" d={target} />}
          {hasTarget && (narrow
            ? <text className="ui-chart__lab" x={x(n - 1)} y={y(runT) - 8} textAnchor="end">target {formatAmount(runT, unit)}</text>
            : <text className="ui-chart__lab" x={x(n - 1) + 8} y={y(runT) + 4}>target {formatAmount(runT, unit)}</text>)}
          {wash && <path className="ui-chart__wash" d={wash} />}
          {shown.length > 1 && <path className="ui-chart__line" d={line} />}
          {shown.map((p, i) => (
            <circle key={p.key} className={i === last || n <= 14 ? 'ui-chart__dot' : 'ui-chart__hit'} cx={x(i)} cy={y(p.cum)} r={i === last || n <= 14 ? 4 : 6}>
              <title>{`${p.label}: ${withUnit(p.cum, unit)} so far${hasTarget ? `, target ${formatAmount(p.cumTarget, unit)}` : ''}`}</title>
            </circle>
          ))}
          {last >= 0 && <text className="ui-chart__lab ui-chart__lab--strong" x={narrow && last === n - 1 ? x(last) : x(last) + 8}
            y={y(cum[last].cum) + (hasTarget && Math.abs(y(cum[last].cum) - y(runT)) < 16 && last === n - 1 ? 18 : -8)}
            textAnchor={last === n - 1 ? (narrow ? 'end' : 'start') : 'middle'}>{formatAmount(cum[last].cum, unit)}</text>}
        </svg>
      </div>
    </Chart>
  );
}
